import React, { useState, useEffect, useRef } from 'react'
import { Button, Row, Col, Nav, NavItem, NavLink, TabContent, TabPane } from '@/components/ui-compat'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button as ShadButton } from '@/components/ui/button'
import { History } from 'lucide-react'
import TenderFormSkeleton from './TenderFormSkeleton'
import * as yup from 'yup'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { toastr } from 'react-redux-toastr'
import { useQuery, useLazyQuery, useMutation } from '@apollo/react-hooks'

import { Form, Formik, FormikProps } from 'formik'
import Input from '../../../components/form/FormikInput'

import BiddersList from './BiddersTable'
import PriceBreakdown, { GRID_MODE } from './PriceBreakdown'
import CreateBidModal from './CreateBidModal'

import { FileManager } from '../../../components/file-manager'

import { Query, Mutation } from '../../../api'
import { useRouter, useParamSwitch, useUser } from '../../../hooks'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import TenderTimeline from './tenderTimeline'
import TenderSummary from './TenderSummary'
import BidsTable from './BidsTable'
import TenderBudgetCard from './TenderBudgetCard'
import TenderStatus, { statuses } from './TenderStatus'

import PlacePicker from '../../../components/PlacePicker'
import AddressForm from '../../../components/form/Address'
import TenderStatusForAnalysis from './TenderStatusForAnalysis'
import * as _ from 'lodash'
import { createContext } from 'react'
import { PriceBreakdownRow } from '../../../reactgrid/PriceBreakdownGrid/types/PriceBreakdownRow'

import { FormikOnChangeComponent } from './FormikOnChangeComponent'
import { useIdle } from 'react-use'
import { PriceBreakdownCellChange } from '../../../reactgrid/PriceBreakdownGrid/types/PriceBreakdownCellChange'
import { TenderStatus as TenderStatusType } from '../../../utils/types'
import { AnalyticGridWrapper } from '../../../reactgrid/AnalyticGrid'
import TenderActivityLog from './TenderActivityLog'
import AiEvaluation from './ai-evaluation'

const Ruler = ({ children, ...props }: any) => (
  <span className="text-sm opacity-0" {...props}>{children}</span>
)

const TopStickyContainer = ({ children, ...props }: any) => (
  <div className="flex items-center" {...props}>{children}</div>
)

const LastUpdatedAtContainer = ({ children, ...props }: any) => (
  <p className="text-right text-[10px] mb-0" {...props}>{children}</p>
)

const FixedWidthTabPane = ({ children, ...props }: any) => (
  <TabPane {...props} className="w-full overflow-x-auto">{children}</TabPane>
)

const ScrollableTabPane = ({ children, ...props }: any) => (
  <TabPane {...props} className="scrollable-tab-pane">{children}</TabPane>
)

const initialRows = []

type ContextCallbackFN = (row: PriceBreakdownRow[]) => PriceBreakdownRow[]

export const PriceBreakdownContext = createContext<{
  priceRows: PriceBreakdownRow[]
  setPriceRows: (priceRows: PriceBreakdownRow[] | ContextCallbackFN) => PriceBreakdownRow[]
  gridMode: GRID_MODE
  tenderStatus?: TenderStatusType | null
  onChange: (changes: PriceBreakdownCellChange[] | boolean) => any
  fetching?: boolean
}>({
  priceRows: initialRows,
  setPriceRows: () => [],
  gridMode: GRID_MODE.DEFAULT,
  tenderStatus: null,
  onChange: () => console.warn('PriceBreakdownContext.onChange not implemented!'),
})

const initialValues = {
  name: '',
  description: '',
  commencementDate: new Date().toISOString(),
  submissionDate: new Date().toISOString(),

  packageManagerId: null,
  enablePriceBreakdown: false,
  bidders: [],
  files: [],
  address: { ...AddressForm.initialValues },
  notifyBiddersOnFiles: false,
}

const TenderForm = () => {
  const user = useUser()
  const { t, i18n } = useTranslation()

  const {
    match,
    history,
    query: { projectId, id: tenderId },
  } = useRouter()

  const { canCreate, canEdit } = useModuleActions('PROCUREMENT', projectId)

  const [{ activeTab, activeRound: round }, setParams] = useParamSwitch()
  const [selectedRound, setSelectedRound] = useState(1)
  const [formError, setFormError] = useState(null)
  const [priceBreakdowns, setPriceBreakdowns] = useState([{ tenderRound: 1 }])
  const [currentPriceBreakdown, setCurrentPriceBreakdown] = useState<any>()
  const [updatedAt, setUpdatedAt] = useState<Date>()

  const [hasChangedSinceSaved, setHasChangedSinceSaved] = useState<boolean>(false)
  const [lastSavedValues, setLastSavedValues] = useState<any>(null)

  const [priceRows, setPriceRows] = useState<PriceBreakdownRow[]>(initialRows)

  // Create Bid Modal state
  const [createBidModalOpen, setCreateBidModalOpen] = useState(false)
  const [activityLogOpen, setActivityLogOpen] = useState(false)
  const [selectedBidderIdForBid, setSelectedBidderIdForBid] = useState<string | undefined>()

  const projectData = useQuery(Query.PROJECT, {
    variables: { id: projectId }, // ROLE SPECIFICATION REMOVED TEMPORARY
  })

  const [loadTender, { data: tenderData, loading: tenderLoading, refetch: refetchTender }] = useLazyQuery(Query.TENDER, {
    variables: { id: tenderId },
    fetchPolicy: 'network-only',
  })
  const [initialized, setInitialized] = useState<{ tenderData: boolean }>({ tenderData: false })

  const [updateTender, updateTenderData] = useMutation(Mutation.UPDATE_TENDER, {
    variables: { id: tenderId },
    refetchQueries: [{ query: Query.TENDER, variables: { id: tenderId } }],
  })

  const [resendNotification, resendNotificationData] = useMutation(
    Mutation.NOTIFY_BIDDER_TENDER_RUNNING,
    {
      onCompleted: () => {
        toastr.success('', t('Procurement_tenders_tender_bidder_successfully_notified'))
      },
    },
  )

  const [setTenderFiles] = useMutation(Mutation.SET_TENDER_FILES)

  const [createTender, createTenderData] = useMutation(Mutation.CREATE_TENDER, {
    refetchQueries: [{ query: Query.TENDERS, variables: { projectId } }],
    onCompleted: ({ createTender }) => {
      history.replace(`${match.url.replace('new', createTender.id)}`)
    },
  })

  const schema = yup.object().shape({
    name: yup
      .string()
      .min(3, t('Procurement_tenders_new_tender_name_min'))
      .required(t('Procurement_tenders_new_tender_name_required')),
    description: yup.string().required(t('Procurement_tenders_new_tender_description_required')),
    commencementDate: yup
      .date()
      .test(
        'commencementDate',
        t('Procurement_tenders_new_tender_commencement_date_must_be_in_future'),
        function (value) {
          if (tender.status && tender.status !== 'PREPARATION') return true

          return moment(value).isAfter(moment())
        },
      )
      .required(t('Procurement_tenders_new_tender_commencememt_date_required')),
    submissionDate: yup
      .date()
      .test(
        'submissionDate',
        t(
          'Procurement_tenders_new_tender_submission_date_must_be_after_commencement',
          'Must be after Commencement Date',
        ),
        function (value) {
          return moment(this.parent.commencementDate).isBefore(moment(value))
        },
      )
      .required(t('Procurement_tenders_new_tender_submission_date_required')),
    packageManagerId: yup
      .string()
      .required(t('Procurement_tenders_new_tender_package_manager_required'))
      .nullable(),
    address: AddressForm.validationSchemaRequired,
  })

  useEffect(() => {
    if (tenderId && tenderId !== 'new') {
      loadTender()
    }
  }, [tenderId])

  useEffect(() => {
    if (tenderData && !initialized.tenderData) {
      const { tender } = tenderData

      let selRound = tender.round
      if (!tender.round && round) {
        selRound = round
      }
      setParams(activeTab, selRound)
      setSelectedRound(selRound)

      if (tender.priceBreakdowns.length) {
        const pbs = tender.priceBreakdowns
        for (let i = 1; i <= tender.round; i++) {
          if (!pbs.find((pb) => pb.tenderRound === i)) {
            pbs.push({ tenderRound: i })
          }
        }

        setPriceBreakdowns(pbs)
        let cpb = pbs.find((pb) => pb.tenderRound === selRound)
        setCurrentPriceBreakdown(cpb)

        if (cpb && cpb?.children?.length)
          setPriceRows(
            cpb.children
              .sort((a, b) => a.idx - b.idx)
              .map(({ parentId, ...rest }, i) => ({
                ...rest,
                ...(parentId ? { parentId } : {}),
                expanded: false,
              })),
          )
      }
      // Hacky
      setTimeout(() => setInitialized((init) => ({ ...init, tenderData: true })), 2000)
    }
  }, [tenderData])

  // Initialize lastSavedValues when tender data is first loaded
  useEffect(() => {
    if (tenderData && tenderData.tender && initialized.tenderData && !lastSavedValues) {
      const initialValues = formikRef?.current?.values
      if (initialValues) {
        setLastSavedValues({ ...initialValues, priceRows })
      }
    }
  }, [tenderData, initialized.tenderData, priceRows])

  useEffect(() => {
    if (!tenderData || !tenderData.tender.id) return

    setShouldAutosave(true)
  }, [priceRows])

  useEffect(() => {
    if (updateTenderData.data) {
      setUpdatedAt(updateTenderData.data.updateTender.updatedAt)
    } else if (tenderData) {
      setUpdatedAt(tenderData.tender.updatedAt)
    }
  }, [tenderData, updateTenderData.data])

  useEffect(() => {
    const priceBreakdown = priceBreakdowns.find((pb) => pb.tenderRound === selectedRound)
    if (!priceBreakdown) setPriceBreakdowns(priceBreakdowns.concat({ tenderRound: selectedRound }))
    //else setCurrentPriceBreakdown(priceBreakdown)//kdyz to tu je, tak se do analysis nenactou data
  }, [selectedRound])

  const tender = tenderData ? tenderData.tender : { statusActions: [], bids: [], files: [] }
  const { id, bidders, files, packageManager, priceBreakdown, ...formValues } = tender
  const users = projectData.data ? projectData.data.project.users.map((u) => u.user) : []
  const isPackageManager = tender?.packageManager?.id === (user as any)?.id
  const isAdminOrOwner = (user as any)?.rights === 'ADMIN' || (user as any)?.rights === 'OWNER'

  const readOnly = tender.status
    ? ['AWARDED', 'CLOSED'].indexOf(tender.status) > -1 || (!isPackageManager && !isAdminOrOwner)
    : false

  const biddersReadOnly = tender.status && ['AWARDED', 'CLOSED'].indexOf(tender.status) > -1
  const status = statuses[tender.status] || {}
  const isNew = tenderId === 'new'
  const hasSomeBids =
    tender.bids.filter((b) => b.round === selectedRound && b.priceBreakdown).length > 0

  // A bid row exists as soon as a bidder opens the portal (a draft is created
  // on first visit), so counting rows overstates the tab badge — 4 bidders who
  // merely looked would show as "Bids (4)". A bid counts as returned once it
  // holds uploaded files, a submitted total, or filled-in prices. The table
  // itself still lists every row: "Last modification" shows who has been active.
  const returnedBidsCount = tender.bids.filter(
    (b) =>
      b.round === selectedRound &&
      ((b.files?.length ?? 0) > 0 ||
        b.totalPrice != null ||
        (b.priceBreakdown?.children?.length ?? 0) > 0),
  ).length

  const isLoading =
    tenderLoading || updateTenderData.loading || createTenderData.loading || tenderLoading

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  // For form initialization, so it doesn't trigger autosave
  // when form is first loaded
  const [shouldAutosave, setShouldAutosave] = useState<boolean>(false)

  const isIdle = useIdle(3000)

  const placePickerOnChange = (address, setFieldValue, setFieldTouched) => {
    let addressFieldValue = {
      city: address.city,
      street: address.street_name + ' ' + address.street_number,
      zip: address.zip,
      country: address.country,
      lat: address.latLng.lat,
      lng: address.latLng.lng,
    }
    setFieldValue('address', addressFieldValue)
    setFieldTouched('address')
  }

  const onRoundChange = (newRound) => {
    setParams(activeTab, newRound)
    setSelectedRound(newRound)
  }

  /**
   * Check if current values are different from last saved values
   */
  const hasActualChanges = (currentValues: any): boolean => {
    // For new tenders (tenderId === 'new'), always return true so save button is enabled
    if (tenderId === 'new') return true

    if (!lastSavedValues) return false // Not yet initialized, no changes

    // Compare relevant fields (excluding metadata like updatedAt)
    const relevantFields = [
      'name', 'description', 'address', 'commencementDate', 'submissionDate',
      'hardcopyRequired', 'hardcopyAddress', 'enablePriceBreakdown',
      'packageManagerId', 'bidders', 'notifyBiddersOnFiles'
    ]

    // Check if any relevant field has changed
    for (const field of relevantFields) {
      if (JSON.stringify(currentValues[field]) !== JSON.stringify(lastSavedValues[field])) {
        return true
      }
    }

    // Check if priceRows have changed
    if (JSON.stringify(priceRows) !== JSON.stringify(lastSavedValues.priceRows)) {
      return true
    }

    return false
  }

  /**
   * Unified save method for both manual submit and autosave
   * @param values - Form values to save
   * @param options - { isManualSave: boolean, setTouched?: function, setFormError?: function }
   */
  const saveTender = async (values: any, options: {
    isManualSave?: boolean
    setTouched?: (touched: any) => void
    setFormError?: (error: string) => void
  } = {}) => {
    const { isManualSave = false, setTouched, setFormError } = options

    try {
      if (id) {
        // Update existing tender
        const fileInfo = values.priceBreakdownFile
          ? {
              file: values.priceBreakdownFile,
              size: values.priceBreakdownFile.size,
            }
          : {}

        let variables = {
          ...values,
          ...fileInfo,
          priceBreakdown: priceRows,
          tenderRound: selectedRound,
          updatedAt: Date.now(),
          hardcopyAddress: undefined,
          skipLog: !isManualSave, // Skip log creation for autosave
        }

        delete variables.files

        await updateTender({
          variables,
        })

        if (isManualSave) {
          // Manual save: show toast notification
          isNew && toastr.success('', t('Procurement_tenders_new_tender_tender_updated_label'))
          setTouched && setTouched({})
        } else {
          // Autosave: log to console
          console.log('✅ Tender auto-saved successfully', new Date().toLocaleTimeString())
        }
      } else {
        // Create new tender
        await createTender({ variables: { ...values, projectId } })

        if (isManualSave) {
          isNew && toastr.success('', t('Procurement_tenders_new_tender_tender_created_label'))
          setTouched && setTouched({})
        }
      }

      // Store the saved values for comparison on next autosave
      setLastSavedValues({ ...values, priceRows })
      setHasChangedSinceSaved(false)
    } catch (error) {
      if (isManualSave) {
        // Manual save: set form error
        setFormError && setFormError(error.message)
      } else {
        // Autosave: log error to console
        console.error('❌ Auto-save failed:', error.message)
      }
      throw error
    }
  }

  const autosave = async () => {
    if (!tenderData || !tenderData.tender.id) return
    if (!hasChangedSinceSaved) return

    if (!updateTenderData.loading && !isSubmitting) {
      const values = formikRef?.current?.values
      if (!values) return

      // Check if there are actual changes before saving
      if (!hasActualChanges(values)) {
        console.log('⏭️  Autosave skipped - no actual changes detected', new Date().toLocaleTimeString())
        setHasChangedSinceSaved(false) // Reset the flag since there are no real changes
        return
      }

      setIsSubmitting(true)

      try {
        await saveTender(values, { isManualSave: false })
      } catch (error) {
        // Error already logged in saveTender
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  useEffect(() => {
    if (!shouldAutosave) return setShouldAutosave(true)
    // Only trigger autosave when user becomes idle (not when they become active)
    // And only for tenders in PREPARATION status
    if (isIdle && tender.status === 'PREPARATION') {
      autosave()
    }
  }, [isIdle, tender.status])

  const onPriceRowsChanged = (changes: PriceBreakdownCellChange[] | boolean) => {
    if (
      typeof changes !== 'boolean' &&
      changes.every((change) => change.type.toLowerCase().includes('chevron'))
    )
      return

    setHasChangedSinceSaved(true)
  }

  const formikRef = useRef<FormikProps<any>>(null)

  // Show skeleton while initial tender data is loading
  if (!isNew && (tenderLoading || !initialized.tenderData)) {
    return <TenderFormSkeleton />
  }

  return (
    <>
      <Ruler id="ruler" />
      <Formik
        innerRef={formikRef}
        validationSchema={readOnly ? undefined : schema}
        enableReinitialize
        initialValues={{
          ...{
            ...initialValues,
            ...{ address: projectData.data && projectData.data.project.address },
          },
          ...formValues,
          bidders: bidders ? bidders.map((b) => b.id) : [],
          packageManagerId: packageManager ? packageManager.id : null,
          files: files,
          priceBreakdowns,
        }}
        validateOnChange
        onSubmit={async (values, { setTouched }) => {
          setFormError(null)
          await saveTender(values, {
            isManualSave: true,
            setTouched,
            setFormError
          })
        }}
      >
        {({ values, setFieldValue, touched, setFieldTouched, errors, submitForm }) => {
          const format = 'DD-MM-YYYY'
          const isTouched = Object.keys(touched).length > 0
          const isSameDay = (a = values.commencementDate, b = values.submissionDate) =>
            moment(a).format(format) === moment(b).format(format)

          // Check if save button should be disabled
          const hasChanges = hasActualChanges(values)
          const isSaveDisabled = !hasChanges || isLoading

          const setField = (name, value) => {
            if (name === 'files' && readOnly) {
              setFieldValue('notifyBiddersOnFiles', true)
            }
            setFieldValue(name, value)
            // setFieldTouched(name)
          }

          const displayAnalysisAndBids =
            !isNew && hasSomeBids && currentPriceBreakdown && tender.status !== 'RUNNING' && values.enablePriceBreakdown

          const displayBids = !isNew && hasSomeBids && tender.status !== 'RUNNING'

          return (
            <Form>
              {!isLoading && tenderData && initialized.tenderData && (
                <FormikOnChangeComponent onChange={() => setHasChangedSinceSaved(true)} />
              )}
              <Row>
                <Col>
                  <div className="flex flex-col xl:flex-row gap-6">
                    <div className="flex-1 min-w-0">
                      <>
                        <div className="sticky top-0 z-10 mb-3 rounded-lg border bg-card p-3 shadow-sm">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold mb-0">
                                {isLoading
                                  ? t('Procurement_tenders_tender_heading_loading', 'Loading ...')
                                  : tender.name || t('Procurement_tenders_new_tender_heading')}
                              </h3>
                              <Badge variant="outline" className={`whitespace-nowrap ${status.badge}`}>{t(status.title)}</Badge>
                            </div>
                            {(isNew ? canCreate : canEdit) && (
                              <Button type={'submit'} color="success" disabled={isSaveDisabled}>
                                {isLoading
                                  ? t('Procurement_tenders_tender_heading_loading')
                                  : id
                                  ? t('Procurement_tenders_new_tender_update_tender_btn')
                                  : t('Procurement_tenders_new_tender_create_tender_btn')}
                                {updatedAt && (
                                  <LastUpdatedAtContainer>
                                    {t('Tender_last_updated_at') + moment(updatedAt).calendar()}
                                  </LastUpdatedAtContainer>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>

                        {!isNew && tender.id && (
                          <TenderBudgetCard tenderId={tender.id} projectId={projectId} running={tender.status === 'RUNNING'} className="mb-4" />
                        )}
                        <div className="tab relative">
                          <Nav tabs>
                            <NavItem>
                              <NavLink
                                className={cn(
                                  { active: activeTab === 'info' },
                                  Object.entries(errors).length > 0 && activeTab !== 'info' && !isLoading && isTouched
                                    ? 'border-red-300 bg-red-50 text-red-600'
                                    : '',
                                )}
                                onClick={() => {
                                  setParams('info', selectedRound)
                                  if (!readOnly) {
                                    setFieldTouched('name')
                                    setFieldTouched('description')
                                    setFieldTouched('commencementDate')
                                    setFieldTouched('submissionDate')
                                    setFieldTouched('packageManagerId')
                                    setFieldTouched('address')
                                  }
                                }}
                              >
                                {t('Tender_form_general')}
                              </NavLink>
                            </NavItem>
                            {!isNew && (
                              <NavItem>
                                <NavLink
                                  className={cn({ active: activeTab === 'bidders' })}
                                  onClick={() => setParams('bidders', selectedRound)}
                                >
                                  {t('Tender_form_bidders_list')}{' '}
                                  {values.bidders.length > 0 ? ` (${values.bidders.length})` : ''}
                                </NavLink>
                              </NavItem>
                            )}
                            {!isNew && values.enablePriceBreakdown && (
                              <NavItem>
                                <NavLink
                                  className={cn({ active: activeTab === 'price' })}
                                  onClick={() => setParams('price', selectedRound)}
                                >
                                  {t('Tender_form_price_breakdown')}
                                </NavLink>
                              </NavItem>
                            )}
                            {!isNew && (
                              <NavItem>
                                <NavLink
                                  className={cn({ active: activeTab === 'documents' })}
                                  onClick={() => setParams('documents', selectedRound)}
                                >
                                  {t('Tender_form_documents')}
                                  {values.files.length > 0 ? ` (${values.files.length})` : ''}
                                </NavLink>
                              </NavItem>
                            )}

                            {displayBids && (
                              <NavItem>
                                <NavLink
                                  className={cn({ active: activeTab === 'bids' })}
                                  onClick={() => setParams('bids', selectedRound)}
                                >
                                  {t('Tender_form_bids')}{' '}
                                  {returnedBidsCount > 0 ? ` (${returnedBidsCount})` : ''}
                                </NavLink>
                              </NavItem>
                            )}
                            {displayAnalysisAndBids && (
                              <NavItem>
                                <NavLink
                                  className={cn({ active: activeTab === 'analysis' })}
                                  onClick={() => setParams('analysis', selectedRound)}
                                >
                                  {t('Tender_form_analysis')}
                                </NavLink>
                              </NavItem>
                            )}
                            {!isNew && (
                              <NavItem>
                                <NavLink
                                  className={cn('flex items-center gap-1.5', {
                                    active: activeTab === 'ai-evaluation',
                                  })}
                                  onClick={() => setParams('ai-evaluation', selectedRound)}
                                >
                                  {t('ai_evaluation')}
                                  <Badge
                                    variant="outline"
                                    className="px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wide border-amber-300 bg-amber-50 text-amber-700"
                                  >
                                    Beta
                                  </Badge>
                                </NavLink>
                              </NavItem>
                            )}

                            {!isNew && (
                              <NavItem className="ml-auto">
                                <ShadButton
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => setActivityLogOpen(true)}
                                  title={t('Tender_form_activity_logs')}
                                >
                                  <History className="h-4 w-4" />
                                </ShadButton>
                              </NavItem>
                            )}

                            {['analysis', 'price'].includes(activeTab) && (
                              <TenderStatusForAnalysis
                                onRoundChange={onRoundChange}
                                initialRound={selectedRound}
                                noItemsInBreakdown={
                                  values.enablePriceBreakdown &&
                                  !(currentPriceBreakdown && currentPriceBreakdown.children) &&
                                  !priceRows.length
                                }
                                noBidders={values.bidders.length === 0}
                                tenderFormTouched={isTouched && !readOnly}
                                hasUnsavedChanges={hasChanges}
                                commencementDateInvalid={
                                  moment().diff(values.commencementDate) > 0 &&
                                  tender.status === 'PREPARATION'
                                }
                                submissionDateInvalid={
                                  moment().diff(values.submissionDate) > 0 &&
                                  tender.status === 'PREPARATION'
                                }
                              />
                            )}
                          </Nav>

                          <TabContent activeTab={activeTab}>
                            <TabPane tabId="info">
                              <div className="grid gap-6">
                                {/* General Information Section */}
                                <div className="grid gap-4">
                                  <h3 className="text-lg font-semibold">{t('Tender_form_general_information')}</h3>
                                  <Input
                                    label={t('Procurement_tenders_new_tender_name_label')}
                                    name="name"
                                    placeholder={t('Procurement_tenders_new_tender_name_placeholder')}
                                    readOnly={readOnly}
                                  />
                                  <Input
                                    label={t('Procurement_tenders_new_tender_description_label')}
                                    type="textarea"
                                    name="description"
                                    placeholder={t(
                                      'Procurement_tenders_new_tender_description_placeholder',
                                    )}
                                    rows={5}
                                    readOnly={readOnly}
                                  />
                                </div>

                                <Input
                                  label={t('Tender_form_enable_price_breakdown')}
                                  type="checkbox"
                                  name="enablePriceBreakdown"
                                  readOnly={readOnly}
                                />

                                {/* Dates Section */}
                                <div className="grid gap-4">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Input
                                      label={t(
                                        'Procurement_tenders_new_tender_commencement_date_label',
                                      )}
                                      type="datetime"
                                      name="commencementDate"
                                      placeholder={t(
                                        'Procurement_tenders_new_tender_commencement_date_placeholder',
                                      )}
                                      readOnly={readOnly || tender.status === 'RUNNING'}
                                      minDate={new Date()}
                                      minTime={
                                        isSameDay(values.commencementDate, moment().toDate())
                                          ? moment().toDate()
                                          : moment().hours(0).minutes(0).seconds(0).toDate()
                                      }
                                      maxTime={moment().hours(23).minutes(59).seconds(59).toDate()}
                                      onChange={(value) => {
                                        setField('commencementDate', moment(value))
                                      }}
                                    />
                                    <Input
                                      minDate={new Date(values.commencementDate)}
                                      minTime={
                                        isSameDay()
                                          ? moment(values.commencementDate).add(1, 'minutes').toDate()
                                          : moment().hours(0).minutes(0).seconds(0).toDate()
                                      }
                                      maxTime={moment().hours(23).minutes(30).seconds(0).toDate()}
                                      onChange={(value) => {
                                        if (moment(value).isBefore(moment(values.commencementDate))) {
                                          setFieldTouched('submissionDate')
                                        }
                                      }}
                                      onBlur={() => {
                                        setFieldTouched('submissionDate')
                                      }}
                                      label={t(
                                        'Procurement_tenders_new_tender_submission_date_label',
                                      )}
                                      type="datetime"
                                      name="submissionDate"
                                      placeholder={t(
                                        'Procurement_tenders_new_tender_submission_date_placeholder',
                                      )}
                                      readOnly={readOnly && tender.status !== 'RUNNING'}
                                    />
                                  </div>
                                  <Input
                                    label={t('Procurement_tenders_new_tender_package_manager_label')}
                                    type="select"
                                    name="packageManagerId"
                                    labelField="name"
                                    valueField="id"
                                    placeholder={t(
                                      'Procurement_tenders_new_tender_package_manager_placeholder',
                                    )}
                                    options={users}
                                    readOnly={readOnly}
                                  />
                                </div>

                                {/* Address Section */}
                                <div className="grid gap-4">
                                  <h3 className="text-lg font-semibold">{t('Tender_form_general_information_address')}</h3>
                                  <PlacePicker
                                    readOnly={readOnly}
                                    onChange={(address, e) => {
                                      placePickerOnChange(address, setFieldValue, setFieldTouched)
                                    }}
                                  />
                                  <AddressForm readOnly={readOnly} name="address" />
                                </div>
                              </div>
                            </TabPane>
                            {!isNew && (
                              <TabPane tabId="bidders">
                                <BiddersList
                                  projectId={projectId}
                                  readOnly={biddersReadOnly}
                                  tenderStatus={tender.status}
                                  selection={values.bidders}
                                  tender={tender}
                                  round={selectedRound}
                                  onResendNotification={(bidderId) => {
                                    resendNotification({
                                      variables: {
                                        tenderId,
                                        bidderId,
                                      },
                                    })
                                  }}
                                  onCreateBid={(bidderId) => {
                                    setSelectedBidderIdForBid(bidderId)
                                    setCreateBidModalOpen(true)
                                  }}
                                  onSelectionChange={(s) => setField('bidders', s)}
                                  winnerId={tender.winnerBidder && tender.winnerBidder.id}
                                />
                              </TabPane>
                            )}
                            {!isNew && values.enablePriceBreakdown && (
                              <ScrollableTabPane tabId="price">
                                <PriceBreakdownContext.Provider
                                  value={{
                                    priceRows,
                                    fetching: !initialized.tenderData,
                                    onChange: onPriceRowsChanged,
                                    // @ts-ignore
                                    setPriceRows,
                                    tenderStatus: tender.status,
                                    gridMode: GRID_MODE.DEFAULT,
                                  }}
                                >
                                  {initialized && tenderData && (
                                    <PriceBreakdown readOnly={readOnly} />
                                  )}
                                </PriceBreakdownContext.Provider>
                              </ScrollableTabPane>
                            )}
                            {!isNew && (
                              <TabPane tabId="documents">
                                {/* @ts-ignore */}
                                <FileManager
                                  readOnly={readOnly || !canEdit}
                                  files={values.files}
                                  showFileSelect
                                  onSelected={(s) => {
                                    setTenderFiles({
                                      variables: {
                                        id: tenderId,
                                        files: [...values.files.map((f) => f.id), ...s],
                                      },
                                    })
                                  }}
                                  onRemoved={(s) => {
                                    setTenderFiles({
                                      variables: {
                                        id: tenderId,
                                        files: [...values.files.map((f) => f.id)].filter(
                                          (f) => s.indexOf(f) === -1,
                                        ),
                                      },
                                    })
                                  }}
                                  directory={'general'}
                                  uploadDirectory={`tender/${tenderId}`}
                                />
                              </TabPane>
                            )}
                            {displayBids && (
                              <TabPane tabId="bids">
                                <BidsTable
                                  bids={tender.bids.filter((b) => b.round === selectedRound)}
                                  winningBidId={
                                    tender.winnerBidder &&
                                    tender.bids.filter(
                                      (b) =>
                                        b.round === tender.round &&
                                        b.bidder.id === tender.winnerBidder.id,
                                    )[0].id
                                  }
                                  enablePriceBreakdown={values.enablePriceBreakdown}
                                  tenderStatus={tender.status}
                                />
                              </TabPane>
                            )}

                            {!isNew &&
                              hasSomeBids &&
                              currentPriceBreakdown &&
                              activeTab === 'analysis' &&
                              tender.status !== 'running' && (
                                <>
                                  <PriceBreakdownContext.Provider
                                    value={{
                                      // priceRows,
                                      fetching: !initialized.tenderData,
                                      onChange: onPriceRowsChanged,
                                      // @ts-ignore
                                      setPriceRows,
                                      tenderStatus: tender.status,
                                      gridMode: GRID_MODE.DEFAULT,
                                    }}
                                  >
                                    {initialized && tenderData && (
                                      <AnalyticGridWrapper
                                        tender={tenderData.tender}
                                        activeRound={selectedRound}
                                      />
                                    )}
                                  </PriceBreakdownContext.Provider>
                                  <FixedWidthTabPane tabId="analysis"></FixedWidthTabPane>
                                </>
                              )}

                            {!isNew && (
                              <TabPane tabId="ai-evaluation">
                                <AiEvaluation tender={tender} round={selectedRound} projectId={projectId} />
                              </TabPane>
                            )}
                          </TabContent>
                        </div>

                        {activeTab === 'info' && (
                          <div className="mt-3">
                            <TenderTimeline />
                          </div>
                        )}
                      </>
                    </div>
                    {activeTab === 'info' && (
                      <div className="order-first w-full min-w-0 shrink-0 xl:order-none xl:w-[280px]">
                        <TenderStatus
                          onRoundChange={onRoundChange}
                          initialRound={selectedRound}
                          noItemsInBreakdown={
                            values.enablePriceBreakdown &&
                            !(currentPriceBreakdown && currentPriceBreakdown.children) &&
                            !priceRows.length
                          }
                          noBidders={values.bidders.length === 0}
                          tenderFormTouched={isTouched && !readOnly}
                          hasUnsavedChanges={hasChanges}
                          commencementDateInvalid={
                            moment().diff(values.commencementDate) > 0 &&
                            tender.status &&
                            ['RUNNING', 'CLOSED'].indexOf(tender.status) === -1
                          }
                          submissionDateInvalid={
                            moment().diff(values.submissionDate) > 0 &&
                            tender.status &&
                            ['RUNNING', 'CLOSED'].indexOf(tender.status) === -1
                          }
                        />
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </Form>
          )
        }}
      </Formik>

      {/* Create Bid Modal */}
      {!isNew && tender && (
        <CreateBidModal
          isOpen={createBidModalOpen}
          toggle={() => {
            setCreateBidModalOpen(false)
            setSelectedBidderIdForBid(undefined)
          }}
          tender={tender}
          bidders={tender.bidders || []}
          currentRound={tender.round || 1}
          preselectedBidderId={selectedBidderIdForBid}
          onBidCreated={() => {
            // Refetch tender data to update bids list and timeline
            if (refetchTender) {
              refetchTender()
            } else {
              loadTender()
            }
          }}
        />
      )}

      {/* Activity Log Modal */}
      <Dialog open={activityLogOpen} onOpenChange={setActivityLogOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{t('Tender_form_activity_logs')}</DialogTitle>
          </DialogHeader>
          {/* p-1.5/-m-1.5: overflow-auto also clips at the padding edge, so the
              scroll box needs interior room for the 4px focus ring. */}
          <div className="flex-1 min-h-0 overflow-auto p-1.5 -m-1.5">
            <TenderActivityLog tenderId={tenderId} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default TenderForm
